/**
 * The clip card's poster, fetched lazily and only for a drawn row.
 *
 * ── Why this one field is not in the queue payload ───────────────────────────────────────────
 * Everything else the clip card draws IS — duration, shape, the set anchor, the day it was
 * performed. `posterUrl` is a SIGNED url, and signing one costs a call to the media service per
 * clip; a queue read carries up to fifty items, so putting it in the payload would mint fifty
 * signatures to draw at most two frames. `GET /clips/{clipId}` answers with it.
 *
 * ── The signed url is read ONCE, and a window focus must not re-mint it ──────────────────────
 * The endpoint mints a NEW signature per read, so the read is the entity's own `clipQueryOptions`,
 * which never goes stale and never refetches on focus — and is the same cache entry the player
 * reads when the card opens it.
 */

import { useQuery } from '@tanstack/react-query';

import { clipQueryOptions } from '~/entities/exercise-clip';

/** Where the lazy read has got to. `ready` is a read that answered, poster or not. */
export type ClipMediaStatus = 'pending' | 'ready' | 'failed';

export interface ClipMedia {
  readonly status: ClipMediaStatus;
  /** The still, or `null` — not ready, or the clip has no poster at all. */
  readonly posterUrl: string | null;
}

/**
 * @param clipId The clip to read.
 * @param wanted Whether the row has been on screen. `false` holds the read at `pending`, which is
 *   the state a frame already draws — the box is sized from the wire, so an off-screen row scrolls
 *   into view at its final size and fills in.
 */
export function useClipMedia(clipId: number, wanted: boolean): ClipMedia {
  const clip = useQuery({ ...clipQueryOptions(clipId), enabled: wanted });
  return {
    status: clip.isPending ? 'pending' : clip.isError ? 'failed' : 'ready',
    posterUrl: clip.data?.media.posterUrl ?? null,
  };
}
